import { IDP_V2_SESSION_ID_NAME } from './idpV2Auth';

type HeartbeatResponse<T> = {
  isSuccess?: boolean;
  message?: string;
  genericModel?: T;
};

type AnalyticsEventInput = {
  event_type: string;
  user_id?: string | null;
  session_id?: string | null;
  payload?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

export function getAnalyticsV3BaseUrl(): string {
  return (import.meta.env.VITE_ANALYTICS_V3_BASE_URL ?? '').replace(/\/$/, '');
}

function createEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `analytics_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export async function sendAnalyticsEvent(input: AnalyticsEventInput): Promise<void> {
  const analyticsBaseUrl = getAnalyticsV3BaseUrl();
  const sessionId = input.session_id ?? localStorage.getItem(IDP_V2_SESSION_ID_NAME);
  if (!analyticsBaseUrl || !sessionId) {
    return;
  }

  const response = await fetch(`${analyticsBaseUrl}/analytics/events`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      event_id: createEventId(),
      event_type: input.event_type,
      user_id: input.user_id ?? undefined,
      session_id: sessionId,
      occurred_at: new Date().toISOString(),
      payload: {
        path: window.location.pathname,
        search: window.location.search,
        visibility_state: document.visibilityState,
        app_version: import.meta.env.VITE_VERSION || null,
        ...(input.payload ?? {})
      },
      metadata: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: navigator.userAgent,
        ...(input.metadata ?? {})
      }
    })
  });

  const json = await response.json().catch(() => ({})) as HeartbeatResponse<unknown>;
  if (!response.ok || !json.isSuccess) {
    throw new Error(json.message || 'Analytics event request failed');
  }
}

export async function sendAnalyticsHeartbeat(sessionId = localStorage.getItem(IDP_V2_SESSION_ID_NAME)): Promise<void> {
  const analyticsBaseUrl = getAnalyticsV3BaseUrl();
  if (!analyticsBaseUrl || !sessionId) {
    return;
  }

  const response = await fetch(`${analyticsBaseUrl}/analytics/heartbeat`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      event_id: createEventId(),
      session_id: sessionId,
      occurred_at: new Date().toISOString(),
      payload: {
        path: window.location.pathname,
        search: window.location.search,
        visibility_state: document.visibilityState,
        app_version: import.meta.env.VITE_VERSION || null
      },
      metadata: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: navigator.userAgent
      }
    })
  });

  const json = await response.json().catch(() => ({})) as HeartbeatResponse<unknown>;
  if (!response.ok || !json.isSuccess) {
    throw new Error(json.message || 'Heartbeat request failed');
  }
}
